import { AlertDetails, JFormik } from '@centerpiece/components';
import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import { AccountBox } from '@mui/icons-material';
import { ContactComponent } from 'components/ContactComponent';
import {
  useFESEditProjectForm,
  useCustomProjectForm,
  useProjectTemplateForm,
} from 'jformik/useProjectForm';
import { useEffect, useRef, useState } from 'react';
import {
  useGetSubscriptionContactsQuery,
  useGetOrganizationsQuery,
  useGetProjectQuery,
  useGetStagesQuery,
  usePatchStageMutation,
  useGetProjectStatesQuery,
  useUpdateProjectMutation,
  usePatchProjectMutation,
  useGetProjectContactsQuery,
  useGetRolesQuery,
  useGetSubscribersQuery,
  useDeleteProjectContactMutation,
  useCreateProjectContactMutation,
  useUpdateProjectContactMutation,
  useGetCommentsQuery,
  useGetContractTypesQuery,
} from 'store/api';
import {
  ProjectContact,
  Subscriber,
  Project,
  ProjectState,
} from 'store/api/models';
import { useTutorialContext } from 'tutorials/tutorial.context';
import { useGetWorkFlowForm } from 'utils/workflow.utils';
import * as Yup from 'yup';
import { useInternalRolesForm } from 'jformik/useStakeholderForm';
import { useMasterEditProjectForm } from 'jformik/useMasterProjectForm';

export interface ProjectInfoTabProps {
  id: number;
  archived?: boolean;
  previewTab?: boolean;
  template?: boolean;
  exampleProject?: Project;
  afterSave?: Function;
}

export const ProjectInfoTab = (props: ProjectInfoTabProps) => {
  const theme = useTheme();
  const formRef = useRef<any>(null);
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  const {
    id = 0,
    template = false,
    archived = false,
    afterSave = () => {},
    ...rest
  } = props;

  const {
    data: project = props.exampleProject || ({} as Project),
    isLoading: projectLoading,
    isFetching: projectFetching,
  } = useGetProjectQuery(
    template
      ? {
          id: id,
          params: {
            nested: 'details',
            template: template,
            archived: archived ? archived : undefined,
          },
        }
      : {
          id: id,
          params: {
            archived: archived ? archived : undefined,
            nested: 'details',
          },
        },
    { skip: !id || !!props.exampleProject }
  );
  const { data: stages = [] } = useGetStagesQuery(
    { params: { project: id, optimized: true } },
    { skip: !id || !!props.exampleProject }
  );
  const [alertDetails, setAlertDetails] = useState<AlertDetails>();
  const [openContact, setOpenContact] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
    callback?: Function;
    initialValues?: any;
  }>({ open: false });
  const getWorkflowForm = useGetWorkFlowForm({ setOpenContact });
  const workflowForms = stages?.map(s => getWorkflowForm(s)).filter(s => s);
  const [patchStage] = usePatchStageMutation();
  const [title, setTitle] = useState<string>(project?.title || '');
  const [state, setState] = useState<ProjectState>(project?.state_r || {});
  const { data: states = [] } = useGetProjectStatesQuery({});
  const [editProject] = useUpdateProjectMutation();
  const [patchProject] = usePatchProjectMutation();
  const { data: project_contacts = [], isLoading: projectContactsLoading } =
    useGetProjectContactsQuery(
      { params: { nested: 'all', project: id } },
      { skip: !id || !!props.exampleProject }
    );
  const { data: roles = [] } = useGetRolesQuery({});
  const { data: subscribers = [] } = useGetSubscribersQuery({
    params: { nested: 'all' },
  });
  const { data: contract_types } = useGetContractTypesQuery({});
  const [deleteProjectContact] = useDeleteProjectContactMutation();
  const [createProjectContact] = useCreateProjectContactMutation();
  const [editProjectContact] = useUpdateProjectContactMutation();
  const [fesValidation, fesForm] = useMasterEditProjectForm({
    id: id,
    setOpenContact,
  });
  const [customValidation, customForm] = useCustomProjectForm({
    id: id,
    edit: true,
  });
  const [templateValidation, templateForm] = useProjectTemplateForm({
    id: id,
    edit: true,
  });
  const internalRolesForm = useInternalRolesForm(setOpenContact);
  const submit = async () => {
    await patchProject({
      id: id,
      title: title,
      state: state?.id,
      state_r: state,
    } as Project);
    afterSave();
  };
  useEffect(() => {
    setTitle(project?.title || '');
  }, [project]);
  const { state: tutorialState, setState: setTutorialState } =
    useTutorialContext();
  const {
    data: comments = [],
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useGetCommentsQuery(
    { params: { nested: 'all', project: `=${id}` } },
    { skip: !id }
  );

  return (
    <Container maxWidth={'xl'}>
      {openContact?.open && (
        <ContactComponent
          close={() => setOpenContact({ open: false })}
          {...openContact}
        />
      )}
      <Box sx={{ display: openContact?.open ? 'none' : 'block' }}>
        <JFormik
          // enableReinitialize={true}
          initialValues={() =>
            ({
              ...project,
              type: project.type_r,
              contract_type: contract_types?.find(
                ct => ct.id === project.contract_type
              ),
              market_segment: project.market_segment_r,
            } as any)
          }
          loading={projectLoading || Object.keys(project)?.length === 0}
          autoSaveForm={true}
          handleSubmit={async (values, formik) => {
            let valuesCpy = {
              ...values,
              title: title,
              state: state?.id,
              state_r: state,
              type: values?.type?.id,
              market_segment: values?.market_segment?.id,
            };
            delete valuesCpy['contacts']; //remove this field as it is handles out async from form
            delete valuesCpy['stakeholders'];
            await editProject(valuesCpy);
            afterSave();
          }}
          // validationSchema={
          //   template
          //     ? templateValidation
          //     : project.category === 'Custom'
          //     ? customValidation
          //     : fesValidation
          // }
          form={
            template
              ? templateForm
              : project.category === 'Custom'
              ? customForm
              : fesForm
          }
        />
      </Box>
    </Container>
  );
};
